import Graph from "../Graph";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import "../../pages/InsightsTab.css";

function fmtPkr(val) {
  return Number(val || 0).toLocaleString("en-US");
}

function getUsername() {
  let user = null;
  try {
    const stored = localStorage.getItem("user");
    if (stored) user = JSON.parse(stored);
  } catch (e) {
    user = null;
  }
  return (
    localStorage.getItem("username") ||
    user?.username ||
    user?.userName ||
    user?.email ||
    ""
  );
}

const Icon = ({ name, className, size = 20, color = "currentColor" }) => {
  const props = {
    width: size,
    height: size,
    fill: "none",
    viewBox: "0 0 24 24",
    stroke: color,
    strokeWidth: 1.8,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    className,
  };
  switch (name) {
    case "chart":
      return (<svg {...props}><path d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>);
    case "coin":
      return (<svg {...props}><path d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>);
    case "check":
      return (<svg {...props}><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>);
    case "close":
      return (<svg {...props}><path d="M6 18L18 6M6 6l12 12" /></svg>);
    case "eye":
      return (<svg {...props}><path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /><path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>);
    case "clock":
      return (<svg {...props}><path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>);
    default:
      return (<svg {...props}><path d="M12 16v-4m0-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>);
  }
};

function SeverityPill({ severity }) {
  const isCrit = severity === "Critical";
  return (
    <span className={`pill ${isCrit ? "critical" : "warning"}`}>
      <span className="tiny-dot" />
      {severity}
    </span>
  );
}

function CategoryPill({ category }) {
  return (
    <span className="category category-rose">
      {category || "THD Current"}
    </span>
  );
}

function InsightModal({ insight, onClose }) {
  if (!insight) return null;

  const points = insight.chartData || [];
  const vals = points.map((p) => p.value);
  const threshold = points[0]?.threshold ?? 0;
  const minVal = points.length ? Math.min(...vals, threshold) : 0;
  const maxVal = points.length ? Math.max(...vals, threshold) : threshold || 1;
  const span = Math.max(maxVal - minVal, 0.1);

  return (
    <div className="insights-overlay">
      <div className="detail-backdrop" onClick={onClose} />
      <div className="modal" role="dialog" aria-modal="true">
        <div className="modal-head">
          <div className="modal-head-row">
            <div>
              <div className="modal-title-row" style={{ display: "flex", gap: "8px", alignItems: "center", marginBottom: "8px" }}>
                <CategoryPill category={insight.category} />
                <SeverityPill severity={insight.severity} />
              </div>
              <h2>{insight.name}</h2>
              <div className="modal-machine">
                {insight.machine} • Detected: <strong>{insight.detected}</strong>
              </div>
            </div>
            <button className="close-btn" onClick={onClose} aria-label="Close modal">
              <Icon name="close" size={18} />
            </button>
          </div>
        </div>

        <div className="modal-body">
          <p style={{ margin: 0, fontSize: "14px", color: "#475569", lineHeight: "1.5" }}>{insight.description}</p>

          <div style={{
            background: "#f8fafc",
            border: "1px solid #e2e8f0",
            borderRadius: "10px",
            padding: "14px 18px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}>
            <div>
              <div style={{ fontSize: "11px", color: "#64748b", fontWeight: "700", textTransform: "uppercase" }}>Estimated Financial Loss</div>
              <div style={{ fontSize: "22px", fontWeight: "800", color: "#dc2626", marginTop: "4px" }}>PKR {fmtPkr(insight.loss)}</div>
            </div>
            <div style={{ fontSize: "12px", textAlign: "right", color: "#475569", lineHeight: "1.6" }}>
              Equipment: <strong>PKR {fmtPkr(insight.equipmentLoss)}</strong><br />
              Downtime: <strong>PKR {fmtPkr(insight.productionLoss)}</strong>
            </div>
          </div>

          {points.length > 0 && (
            <div className="section">
              <p className="section-title">
                {insight.chartParam} ({insight.chartUnit}) • Threshold: {insight.warningThreshold}
              </p>
              <div style={{ background: "#fff", padding: "14px", borderRadius: "10px", border: "1px solid #e2e8f0" }}>
                <div style={{ height: "220px", width: "100%", marginTop: "10px" }}>
                  <Graph
                    labels={points.map((p, i) => p.time || p.timestamp || `Point ${i + 1}`)}
                    data={points.map((p) => p.value)}
                    title={insight.chartParam}
                    options={{
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { display: false }
                      },
                      scales: {
                        x: { ticks: { maxTicksLimit: 6 } },
                        y: {
                          title: { display: true, text: insight.chartUnit },
                          beginAtZero: false
                        }
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          <div className="section">
            <p className="section-title">Likely Root Causes</p>
            <ul className="bullet-list">
              {insight.reasons?.map((r, i) => (
                <li key={i} className="bullet-item">
                  <span className="reason-bullet" />
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="section">
            <p className="section-title">Recommended Fixes</p>
            <ul className="bullet-list">
              {insight.fixes?.map((f, i) => (
                <li key={i} className="bullet-item">
                  <Icon name="check" className="tick-icon" size={14} />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>

          {insight.occurrences?.length > 1 && (
            <div className="section">
              <p className="section-title">Occurrence Windows</p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginTop: "6px" }}>
                {insight.occurrences.map((o, i) => (
                  <span key={i} className="ghost-btn" style={{ height: "auto", padding: "4px 8px", fontSize: "12px", display: "inline-flex", alignItems: "center", gap: "6px" }}>
                    <Icon name="clock" size={12} /> {o}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ThdCurrent({ fromDate, toDate, machine, severity, onDataLoaded }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [insights, setInsights] = useState([]);
  const [summary, setSummary] = useState({ totalLoss: 0, totalCost: 0, totalSavings: 0, criticalCount: 0, warningCount: 0 });
  const [modalId, setModalId] = useState(null);

  const fetchThdCurrentInsights = useCallback(async () => {
    const username = getUsername();
    if (!username) {
      setError("No authenticated user session found.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const res = await axios.get("/api/ensight/thd_current", {
        params: {
          username,
          machine: machine || "All",
          severity: severity || "All",
          from: fromDate,
          to: toDate,
        },
      });

      if (res.data && res.data.status === "success") {
        const fetchedInsights = res.data.insights || [];
        const fetchedSummary = res.data.summary || { totalLoss: 0, totalCost: 0, totalSavings: 0, criticalCount: 0, warningCount: 0 };

        setInsights(fetchedInsights);
        setSummary(fetchedSummary);

        if (onDataLoaded) {
          onDataLoaded({
            insights: fetchedInsights,
            summary: fetchedSummary,
          });
        }
      } else {
        setError(res.data?.message || "Failed to fetch THD Current insights.");
        setInsights([]);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to fetch THD Current insights.");
      setInsights([]);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, machine, severity, onDataLoaded]);

  useEffect(() => {
    fetchThdCurrentInsights();
  }, [fetchThdCurrentInsights]);

  const activeModalInsight = useMemo(() => {
    return insights.find((i) => i.id === modalId) || null;
  }, [insights, modalId]);

  if (loading) {
    return (
      <div className="panel" style={{ minHeight: 320, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ color: "#64748b", fontWeight: 600 }}>Loading THD Current Insights...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="panel empty-state">
        <div>
          <div className="empty-icon"><Icon name="info" size={28} /></div>
          <p className="empty-title">{error}</p>
          <p className="empty-desc">Check backend connection and press Apply to retry</p>
        </div>
      </div>
    );
  }

  if (insights.length === 0) {
    return (
      <div className="panel empty-state">
        <div>
          <div className="empty-icon"><Icon name="info" size={28} /></div>
          <p className="empty-title">No THD current insights detected in this period</p>
          <p className="empty-desc">Try selecting a 30D date range or adjusting filters</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="panel table-wrap">
        <div className="table-head">
          <p className="table-title">{insights.length} insight{insights.length !== 1 ? "s" : ""} detected</p>
          <p className="table-sub">Sorted: Critical first</p>
        </div>
        <div className="scroll-x">
          <table>
            <thead>
              <tr>
                <th>Insight</th>
                <th>Machine</th>
                <th>Category</th>
                <th>Severity</th>
                <th>Detected</th>
                <th style={{ textAlign: "right" }}>Loss (PKR)</th>
                <th style={{ textAlign: "center" }}>Details</th>
              </tr>
            </thead>
            <tbody>
              {insights.map((row) => (
                <tr key={row.id}>
                  <td style={{ boxShadow: `inset 3px 0 0 ${row.severity === "Critical" ? "#ef4444" : "#f59e0b"}` }}>
                    {row.name}
                  </td>
                  <td>{row.machine}</td>
                  <td><CategoryPill category={row.category} /></td>
                  <td><SeverityPill severity={row.severity} /></td>
                  <td style={{ color: "#64748b", fontSize: 12 }}>{row.detected}</td>
                  <td className="numeric">
                    {row.loss > 0 ? fmtPkr(row.loss) : <span className="muted-cell">—</span>}
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <button className="action-btn" onClick={() => setModalId(row.id)}>
                      <Icon name="eye" size={14} />
                      <span style={{ marginLeft: 6 }}>View</span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="totals">
                <td colSpan={5} style={{ fontSize: 12, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                  Totals ({insights.length} rows)
                </td>
                <td className="numeric" style={{ fontWeight: 900, color: "#334155" }}>{fmtPkr(summary.totalLoss)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {modalId && <InsightModal insight={activeModalInsight} onClose={() => setModalId(null)} />}
    </>
  );
}
